// Vista de time entries por proyecto + summary billable/non-billable.

import { useState } from 'react';

import { projectsApi } from '../api';
import { useTimeEntries } from '../hooks/useProjects';

interface Props {
  projectId: string | null;
}

export function TimeEntries({ projectId }: Props) {
  const { data, summary, loading, error, refetch } = useTimeEntries(projectId);
  const [hours, setHours] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [billable, setBillable] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  if (!projectId) {
    return (
      <div className="text-sm text-neutral-500">
        Selecciona un proyecto para ver / registrar horas.
      </div>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hours) return;
    setSubmitting(true);
    setSubmitErr(null);
    try {
      await projectsApi.createTimeEntry(projectId, {
        hours,
        date,
        description,
        billable,
      });
      setHours('');
      setDescription('');
      await refetch();
    } catch (e) {
      setSubmitErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm">
        {summary && (
          <div className="text-neutral-700">
            <strong>Total:</strong> {summary.total} h ·{' '}
            <span className="text-green-700">Billable: {summary.billable} h</span> ·{' '}
            <span className="text-neutral-500">Overhead: {summary.non_billable} h</span>
          </div>
        )}
      </div>

      <form onSubmit={submit} className="border rounded p-3 space-y-2 bg-white">
        <div className="flex flex-wrap gap-2 items-end">
          <label className="text-xs">
            <span className="block">Horas</span>
            <input
              type="number"
              step="0.25"
              min="0.25"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              className="border rounded px-2 py-1 text-sm w-24"
              required
            />
          </label>
          <label className="text-xs">
            <span className="block">Fecha</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
              required
            />
          </label>
          <label className="text-xs flex items-center gap-1 mt-4">
            <input
              type="checkbox"
              checked={billable}
              onChange={(e) => setBillable(e.target.checked)}
            />
            Billable
          </label>
          <button
            type="submit"
            disabled={submitting}
            className="bg-blue-600 text-white text-sm rounded px-3 py-1 disabled:opacity-40"
          >
            {submitting ? 'Guardando…' : 'Registrar'}
          </button>
        </div>
        <input
          type="text"
          placeholder="Descripción (opcional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="border rounded px-2 py-1 text-sm w-full"
        />
        {submitErr && <div className="text-xs text-red-600">{submitErr}</div>}
      </form>

      {loading && !data && (
        <div className="text-sm text-neutral-500">Cargando time entries…</div>
      )}
      {error && <div className="text-sm text-red-600">Error: {error}</div>}
      {data && data.length === 0 && (
        <div className="text-sm text-neutral-500">Sin horas registradas aún.</div>
      )}
      {data && data.length > 0 && (
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left border-b">
              <th className="px-3 py-2">Fecha</th>
              <th className="px-3 py-2">Horas</th>
              <th className="px-3 py-2">Tipo</th>
              <th className="px-3 py-2">Descripción</th>
            </tr>
          </thead>
          <tbody>
            {data.map((t) => (
              <tr key={t.id} className="border-b">
                <td className="px-3 py-2">{t.date}</td>
                <td className="px-3 py-2">{t.hours}</td>
                <td className="px-3 py-2">
                  {t.billable ? (
                    <span className="text-green-700">Billable</span>
                  ) : (
                    <span className="text-neutral-500">Overhead</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-neutral-600">
                  {t.description || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
